import type { CharacterSave } from '../../game/character';
import type { Game } from '../../game/Game';
import { countOf } from '../../game/inventory';
import type { Progression } from '../../game/Progression';
import { type QuestDef, abandon, isReadyToTurnIn, registerQuests } from '../../game/quests';
import { bump, ui } from '../../ui/app/store';
import { extraSettings } from '../economy/saveExt';
import { layoutOrNull, overworldLayouts } from '../travel/world';
import {
  BOUNTY_NPC,
  type BountyContext,
  type BountyKind,
  bountyQuest,
  bountyZone,
  decodeBounty,
  generateBounties,
  isBountyId,
} from './bounties';
import {
  type DayRollover,
  RIFT_RUN_COST,
  type RestContext,
  advanceDay,
  markSessionClock,
  restStatus,
  rollDaily,
  rollWeekly,
  tickRealClock,
} from './dayCycle';

/**
 * Game glue for the Bounty Board: builds the world context from the
 * registered zones, keeps bounty quest definitions registered, rolls the
 * character's day/week, turns counters into quest events and rests.
 */

// ---------------------------------------------------------------- world context

let ctxCache: { key: string; ctx: BountyContext } | null = null;

export function bountyContext(): BountyContext {
  const layouts = overworldLayouts();
  const key = layouts.map((l) => l.id).join(',');
  if (ctxCache?.key === key) return ctxCache.ctx;
  const zones = layouts.map((l) => bountyZone(l, layoutOrNull));
  ctxCache = { key, ctx: { zones } };
  return ctxCache.ctx;
}

// ---------------------------------------------------------------- registration

const registered = new Set<string>();
let registeredCtx: BountyContext | null = null;

export function charBountyIds(c: CharacterSave): string[] {
  return [
    ...c.daily.bounties,
    ...c.weekly.bounties,
    ...Object.keys(c.quests.active).filter(isBountyId),
  ];
}

/** Registers quest defs for bounty ids (rebuilt when the world context changes). */
export function ensureRegistered(ids: Iterable<string>, ctx: BountyContext): void {
  if (ctx !== registeredCtx) {
    registered.clear();
    registeredCtx = ctx;
  }
  const defs: QuestDef[] = [];
  for (const id of ids) {
    if (!isBountyId(id) || registered.has(id)) continue;
    const q = bountyQuest(id, ctx);
    if (!q) continue;
    defs.push(q);
    registered.add(id);
  }
  if (defs.length) registerQuests(defs);
}

/** Before any character plays: register every saved bounty with names only (no zone data yet). */
export function registerSaved(chars: CharacterSave[]): void {
  const lite: BountyContext = { zones: [] };
  const defs = chars.flatMap((c) =>
    charBountyIds(c)
      .map((id) => bountyQuest(id, lite))
      .filter((q): q is QuestDef => !!q),
  );
  if (defs.length) registerQuests(defs);
}

// ---------------------------------------------------------------- day / week sync

export interface SyncResult {
  day: DayRollover | null;
  newWeek: boolean;
  /** Ready bounties of a finished day/week that were paid out automatically. */
  autoClaimed: QuestDef[];
}

/** Claims ready bounties of past periods and drops the rest. */
function retire(p: Progression, kind: BountyKind, ctx: BountyContext): QuestDef[] {
  const c = p.char;
  const cur = kind === 'daily' ? c.daily.day : c.weekly.week;
  const stale = Object.keys(c.quests.active).filter((id) => {
    const b = decodeBounty(id);
    return !!b && b.kind === kind && b.period !== cur;
  });
  ensureRegistered(stale, ctx);
  const claimed: QuestDef[] = [];
  for (const id of stale) {
    const q = isReadyToTurnIn(c, id) ? p.completeQuest(id) : null;
    if (q) claimed.push(q);
    else abandon(c, id);
  }
  return claimed;
}

/** Rolls the character to the account day/week and makes sure its board is populated. */
export function syncCharacter(p: Progression): SyncResult {
  const c = p.char;
  const a = p.account;
  const ctx = bountyContext();
  const out: SyncResult = { day: null, newWeek: false, autoClaimed: [] };
  const day = rollDaily(c, a);
  if (day) {
    out.day = day;
    out.autoClaimed.push(...retire(p, 'daily', ctx));
  }
  if (!c.daily.bounties.length)
    c.daily.bounties = generateBounties('daily', a.day, c.id, c.level, ctx);
  if (rollWeekly(c, a)) {
    out.newWeek = true;
    out.autoClaimed.push(...retire(p, 'weekly', ctx));
  }
  if (!c.weekly.bounties.length)
    c.weekly.bounties = generateBounties('weekly', a.week, c.id, c.level, ctx);
  ensureRegistered(charBountyIds(c), ctx);
  return out;
}

// ---------------------------------------------------------------- claiming

const hooked = new WeakSet<Progression>();

/** Bookkeeping for every bounty completion (panel, Quill's dialogue or day-end payout). */
export function hookProgression(p: Progression): void {
  if (hooked.has(p)) return;
  hooked.add(p);
  p.on('questCompleted', ({ quest }) => {
    const b = decodeBounty(quest.id);
    if (!b) return;
    const c = p.char;
    const st = b.kind === 'daily' ? c.daily : c.weekly;
    const period = b.kind === 'daily' ? c.daily.day : c.weekly.week;
    if (b.period === period && !st.bountiesDone.includes(quest.id)) st.bountiesDone.push(quest.id);
    const key = b.kind === 'daily' ? 'bountiesDaily' : 'bountiesWeekly';
    c.counters[key] = (c.counters[key] ?? 0) + 1;
    const seals = quest.rewards.items?.find((i) => i.id === 'bounty_seal')?.count ?? 0;
    c.counters.sealsEarned = (c.counters.sealsEarned ?? 0) + seals;
  });
}

export function bountyState(c: CharacterSave, id: string): 'offered' | 'active' | 'ready' | 'done' {
  const b = decodeBounty(id);
  const done = b?.kind === 'weekly' ? c.weekly.bountiesDone : c.daily.bountiesDone;
  if (done.includes(id)) return 'done';
  if (!c.quests.active[id]) return 'offered';
  return isReadyToTurnIn(c, id) ? 'ready' : 'active';
}

export function acceptBounty(p: Progression, id: string): boolean {
  const c = p.char;
  const b = decodeBounty(id);
  if (!b) return false;
  const offers = b.kind === 'daily' ? c.daily.bounties : c.weekly.bounties;
  if (!offers.includes(id) || bountyState(c, id) !== 'offered') return false;
  ensureRegistered([id], bountyContext());
  return p.acceptQuest(id);
}

export function claimBounty(p: Progression, id: string): QuestDef | null {
  if (bountyState(p.char, id) !== 'ready') return null;
  return p.completeQuest(id);
}

// ---------------------------------------------------------------- counters → quest events

const COLOSSI = ['cinderhide', 'gloomfang', 'rimebound', 'dunestrider'];
const DEEPVAULT = ['drowned_warden', 'tide_siren'];
const SIEGE = ['hellhound_kharos', 'vhalzeroth', 'vhalzeroth_phantom'];

function trackerValues(c: CharacterSave, ctx: BountyContext): Record<string, number> {
  const kills = (ids: Iterable<string>) => {
    let n = 0;
    for (const id of ids) n += c.counters[`kill_${id}`] ?? 0;
    return n;
  };
  const fieldBosses = new Set(
    ctx.zones.flatMap((z) => z.packs.filter((p) => p.fieldBoss).map((p) => p.def)),
  );
  return {
    'ctr:temper': c.counters.temperAttempts ?? 0,
    'ctr:battle': c.counters.battleItemsUsed ?? 0,
    'ctr:dailies': c.counters.bountiesDaily ?? 0,
    'ctr:rift': c.counters.riftRuns ?? 0,
    'ctr:colossus': kills(COLOSSI),
    'ctr:deepvault': kills(DEEPVAULT),
    'ctr:siege': kills(SIEGE),
    'ctr:fieldboss': kills(fieldBosses),
  };
}

const baselines = new Map<string, Map<string, number>>();
const riftSeen = new Map<string, { day: number; energy: number }>();

/**
 * Turns counter increases into `use` quest events for counter bounties, and
 * counts Rift runs from Rift Energy spent (50 per run).
 */
export function pumpTrackers(p: Progression, ctx: BountyContext): void {
  const c = p.char;
  const seen = riftSeen.get(c.id);
  if (seen && seen.day === c.daily.day && c.daily.riftEnergy < seen.energy) {
    c.counters.riftRuns =
      (c.counters.riftRuns ?? 0) +
      Math.max(1, Math.round((seen.energy - c.daily.riftEnergy) / RIFT_RUN_COST));
  }
  riftSeen.set(c.id, { day: c.daily.day, energy: c.daily.riftEnergy });

  const vals = trackerValues(c, ctx);
  const base = baselines.get(c.id);
  if (!base) {
    baselines.set(c.id, new Map(Object.entries(vals)));
    return;
  }
  for (const [target, v] of Object.entries(vals)) {
    const last = base.get(target) ?? v;
    for (let i = 0; i < Math.min(50, v - last); i++) p.questEvent({ type: 'use', target });
    base.set(target, v);
  }
}

/** Counts battle items actually consumed through the hotbar. */
export function wrapBattleItems(game: Game): void {
  const orig = game.useBattleItem.bind(game);
  game.useBattleItem = (slot: number) => {
    const p = game.progression;
    const id = p?.char.battleSlots[slot] ?? null;
    const before = p && id ? countOf(p.char, id) : 0;
    orig(slot);
    if (p && id && countOf(p.char, id) < before)
      p.char.counters.battleItemsUsed = (p.char.counters.battleItemsUsed ?? 0) + 1;
  };
}

// ---------------------------------------------------------------- resting

/** Town, Haven Isle, or the board itself (the bounty panel opened from Quill). */
export function atRestSpot(game: Game): boolean {
  const kind = game.zone?.layout.kind;
  return (
    kind === 'town' ||
    kind === 'haven' ||
    (ui.panel.value === 'bounty' && ui.panelArg.value === BOUNTY_NPC)
  );
}

export function restContext(game: Game): RestContext {
  const a = game.account;
  return {
    sessionDays: a.settings.sessionDays,
    relaxed: !!extraSettings(a).restAnytime,
    atRestSpot: atRestSpot(game),
  };
}

function announceSync(game: Game, r: SyncResult): void {
  if (r.day && (r.day.restRift > 0 || r.day.restColossus > 0))
    game.hud.toast(
      `Rested bonus: Rift +${r.day.restRift} · Colossus +${r.day.restColossus}`,
      '#9be07a',
    );
  if (r.autoClaimed.length)
    game.hud.toast(
      `Unclaimed bounties paid out: ${r.autoClaimed.map((q) => q.name).join(', ')}`,
      '#d0405a',
    );
}

/** `game.restDay()`: advance the session day if the daily core is done (or Rest-anytime is on). */
export function restDay(game: Game): boolean {
  const p = game.progression;
  if (!p) return false;
  const st = restStatus(p.char, restContext(game));
  if (!st.ok) {
    game.notify(st.reason, '#e5484d');
    return false;
  }
  const a = game.account;
  const newWeek = advanceDay(a);
  p.char.counters.restDays = (p.char.counters.restDays ?? 0) + 1;
  const r = syncCharacter(p);
  game.hud.announce(
    newWeek ? `Week ${a.week} begins — Day ${a.day}` : `Day ${a.day} begins`,
    'info',
  );
  announceSync(game, r);
  game.save();
  bump();
  return true;
}

/** Per-frame system (throttled): clock, rollover, registration, trackers. */
export function bountySystem(): (game: Game, dt: number) => void {
  let acc = 0;
  return (game, dt) => {
    acc += dt;
    if (acc < 0.25) return;
    acc = 0;
    const p = game.progression;
    if (!p) return;
    hookProgression(p);
    const a = game.account;
    if (a.settings.sessionDays) markSessionClock(a);
    else if (tickRealClock(a, Date.now()) > 0)
      game.hud.announce(`A new day dawns — Day ${a.day}`, 'info');
    const r = syncCharacter(p);
    if (r.day || r.newWeek || r.autoClaimed.length) {
      announceSync(game, r);
      game.store.saveSoon(a);
      bump();
    }
    pumpTrackers(p, bountyContext());
  };
}
